/**
 * US ABA routing number (9 digits, ABA checksum) and account number (1–17 digits). No IBAN.
 * @module banking/schemes/us-aba
 */

import type { BankingValidationError } from '../validation';
import { BankAccountScheme, type BankAccountSchemeDefinition } from './bank-account-scheme';
import {
  bicErrors,
  compactDigits,
  normalizeBic,
  REQUIRED_ERRORS,
  territoryCountry
} from './scheme-helpers';

/** Countries served by ABA routing numbers; the first is the default. */
export const US_ABA_COUNTRIES: readonly string[] = ['US', 'AS', 'GU', 'MP', 'PR', 'VI', 'UM'];
const ROUTING_NUMBER_REGEX = /^\d{9}$/;
/** NACHA's DFI account number field holds up to 17 characters. */
const ACCOUNT_NUMBER_REGEX = /^\d{1,17}$/;
const ABA_WEIGHTS = [3, 7, 1, 3, 7, 1, 3, 7, 1] as const;

/** ABA checksum: digits weighted 3, 7, 1 repeating sum to a multiple of 10. */
export function isValidAbaRoutingNumber(routingNumber: string): boolean {
  if (!ROUTING_NUMBER_REGEX.test(routingNumber)) return false;

  const sum = ABA_WEIGHTS.reduce(
    (total, weight, i) => total + weight * Number(routingNumber[i]),
    0
  );
  return sum % 10 === 0;
}

export const usAbaScheme: BankAccountSchemeDefinition = {
  scheme: BankAccountScheme.US_ABA,

  validate(account) {
    const errors: BankingValidationError[] = [];
    const routingNumber = compactDigits(account.bankIdentifier);
    const accountNumber = compactDigits(account.accountNumber);

    if (!routingNumber) errors.push(REQUIRED_ERRORS.bankIdentifier);
    else if (!ROUTING_NUMBER_REGEX.test(routingNumber)) {
      errors.push({ code: 'bankIdentifierInvalid', message: 'Routing number must be 9 digits' });
    } else if (!isValidAbaRoutingNumber(routingNumber)) {
      errors.push({
        code: 'routingChecksumFailed',
        message: 'Routing number fails the ABA checksum'
      });
    }
    if (!accountNumber) errors.push(REQUIRED_ERRORS.accountNumber);
    else if (!ACCOUNT_NUMBER_REGEX.test(accountNumber)) {
      errors.push({
        code: 'accountNumberInvalid',
        message: 'Account number must be 1 to 17 digits'
      });
    }
    errors.push(...territoryCountry(account.countryCode, US_ABA_COUNTRIES).errors);
    errors.push(...bicErrors(account.bic));

    return { valid: errors.length === 0, errors };
  },

  normalize(account) {
    const routingNumber = compactDigits(account.bankIdentifier);
    const accountNumber = compactDigits(account.accountNumber);
    const { country: countryCode } = territoryCountry(account.countryCode, US_ABA_COUNTRIES);
    if (!routingNumber || !ROUTING_NUMBER_REGEX.test(routingNumber)) return null;
    if (!accountNumber || !ACCOUNT_NUMBER_REGEX.test(accountNumber)) return null;
    if (!countryCode) return null;

    return {
      scheme: BankAccountScheme.US_ABA,
      countryCode,
      iban: null,
      bankIdentifier: routingNumber,
      accountNumber,
      bic: normalizeBic(account.bic)
    };
  },

  toIban() {
    return null;
  }
};
